/**
 * True when editing inventory and only unit cost changed (no qty / conversion stock change).
 */
export function isUnitCostOnlyChange(formValues, editData, editFinishedProductId) {
  if (!editFinishedProductId || !editData?.id) return false;

  const originalUnitCost = parseFloat(editData.unit_cost) || 0;
  const currentUnitCost = parseFloat(formValues.unitCost) || 0;
  const originalConvertionUnitId = editData.convertion_unit ?? null;
  const currentConvertionUnitId = formValues.convertionUnit?.id ?? null;
  const isOriginalBaseUnit = originalConvertionUnitId === 4;
  const isCurrentBaseUnit =
    currentConvertionUnitId === 4 || formValues.convertionUnit?.name === 'Unit';
  const originalConvertionQty =
    parseInt(editData.convertion_qty, 10) || (isOriginalBaseUnit ? 1 : 0);
  const currentConvertionQty =
    parseInt(formValues.convertionQty, 10) || (isCurrentBaseUnit ? 1 : 0);

  let hasStockChange;

  if (editData.partial === 1) {
    const originalPartialQty = parseFloat(editData.qty) || 0;
    const currentPartialQty = parseFloat(formValues.partialQty) || 0;
    hasStockChange =
      originalPartialQty !== currentPartialQty ||
      originalConvertionQty !== currentConvertionQty ||
      originalConvertionUnitId !== currentConvertionUnitId;
  } else {
    const originalQty = parseInt(editData.unit_qty ?? editData.qty, 10) || 0;
    const currentQty = parseInt(formValues.qty, 10) || 0;
    hasStockChange =
      originalQty !== currentQty ||
      originalConvertionQty !== currentConvertionQty ||
      originalConvertionUnitId !== currentConvertionUnitId;
  }

  const hasUnitCostChange = originalUnitCost !== currentUnitCost;
  return hasUnitCostChange && !hasStockChange;
}

function normalizeConvertionQty(convertionQty, convertionUnitId, convertionUnitName) {
  const isBaseUnit = convertionUnitId === 4 || convertionUnitName === 'Unit';
  return parseFloat(convertionQty) || (isBaseUnit ? 1 : 0);
}

/**
 * True when editing a finished product (not partial) and conversion qty changed.
 * Only shown when this finished product already has partial stock (moved to partial).
 */
export function isFinishedConvertionQtyChange(formValues, editData, editFinishedProductId) {
  if (!editFinishedProductId || !editData?.id || editData.partial === 1) return false;
  if (Number(editData.avaliable_partial) !== 1) return false;

  const originalConvertionQty = normalizeConvertionQty(
    editData.convertion_qty,
    editData.convertion_unit,
    null
  );
  const currentConvertionQty = normalizeConvertionQty(
    formValues.convertionQty,
    formValues.convertionUnit?.id,
    formValues.convertionUnit?.name
  );

  return originalConvertionQty !== currentConvertionQty;
}

export function getConvertionQtyRefillWarningContent(formValues, editData) {
  const originalConvertionQty = normalizeConvertionQty(
    editData.convertion_qty,
    editData.convertion_unit,
    null
  );
  const newConvertionQty = normalizeConvertionQty(
    formValues.convertionQty,
    formValues.convertionUnit?.id,
    formValues.convertionUnit?.name
  );
  const unitLabel = formValues.convertionUnit?.short_name || 'unit';

  return {
    message: `Conversion qty is changing from ${originalConvertionQty} to ${newConvertionQty} ${unitLabel}. Partial stock with autofill enabled will refill using ${newConvertionQty} ${unitLabel} per roll from now on.`,
    continueQuestion: 'Do you want to save this change?',
    highlightText: 'autofill',
    title: 'Conversion Qty Change',
  };
}
